import { useCallback, useEffect, useRef, useState } from "react";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import { adminService } from "../../features/admin/service";
import { formatIST } from "../../utils/dateUtils";
import { useAdminToast } from "../../context/AdminToastContext";
import Pagination from "../../components/admin/Pagination";
import type { RagFailedDeal, RagIndexingConfig, RagMissingDeal, RagReindexAllStatus } from "../../features/admin/types";

const CATEGORY_LABEL: Record<string, string> = {
  openrouter_credits: "OpenRouter — insufficient credits",
  openrouter_rate_limit: "OpenRouter — rate limited",
  openrouter_auth: "OpenRouter — auth error",
  pinecone_quota: "Pinecone — quota/full",
  pinecone_error: "Pinecone error",
  network_timeout: "Network timeout",
  bad_doc_shape: "Bad doc shape",
  other: "Other",
};

const CATEGORY_COLOR: Record<string, string> = {
  openrouter_credits: "bg-red-900/60 text-red-300",
  openrouter_rate_limit: "bg-amber-900/60 text-amber-300",
  openrouter_auth: "bg-red-900/60 text-red-300",
  pinecone_quota: "bg-amber-900/60 text-amber-300",
  pinecone_error: "bg-orange-900/60 text-orange-300",
  network_timeout: "bg-amber-900/60 text-amber-300",
  bad_doc_shape: "bg-purple-900/60 text-purple-300",
  other: "bg-gray-700 text-gray-300",
};

const REINDEX_POLL_MS = 4000;

export default function AdminIndexingPage() {
  const { showError } = useAdminToast();
  const [config, setConfig] = useState<RagIndexingConfig | null>(null);
  const [items, setItems] = useState<RagFailedDeal[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { if (error) showError(error); }, [error, showError]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rowResult, setRowResult] = useState<Record<string, "ok" | "fail">>({});

  const [reindexStatus, setReindexStatus] = useState<RagReindexAllStatus | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [missingItems, setMissingItems] = useState<RagMissingDeal[] | null>(null);
  const [missingSkipped, setMissingSkipped] = useState(false);
  const [missingScannedAt, setMissingScannedAt] = useState<string | null>(null);
  const [missingLoading, setMissingLoading] = useState(true);
  const [missingError, setMissingError] = useState<string | null>(null);
  useEffect(() => { if (missingError) showError(missingError); }, [missingError, showError]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminService.listFailedRagDeals({ page, page_size: pageSize });
      setItems(data.items);
      setTotal(data.total);
    } catch (err) {
      console.error("Failed to load failed RAG deals:", err);
      setError("Failed to load failed-indexing deals.");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    adminService
      .getRagIndexingConfig()
      .then(setConfig)
      .catch((err) => console.error("Failed to load RAG indexing config:", err));
  }, []);

  const loadMissingScan = useCallback(async () => {
    setMissingLoading(true);
    setMissingError(null);
    try {
      const data = await adminService.getMissingRagScan();
      setMissingItems(data.items);
      setMissingSkipped(data.skipped);
      setMissingScannedAt(data.scanned_at);
    } catch (err) {
      console.error("Failed to load missing-from-Pinecone scan:", err);
      setMissingError("Failed to load — see console.");
    } finally {
      setMissingLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadMissingScan();
  }, [loadMissingScan]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  // Purely read-only display of whichever scheduled job last ran (30-min
  // flagged-only auto-retry, or the 24h full diff) — nothing on this page
  // triggers either of them manually. Still polls while "running" so the
  // status line updates live if you happen to have the page open when the
  // background job kicks in, but page-load never itself starts a run.
  const pollStatus = useCallback(() => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const status = await adminService.getRagReindexAllStatus();
        setReindexStatus(status);
        if (status.state !== "running") {
          stopPolling();
          void load();
        }
      } catch (err) {
        console.error("Failed to poll reindex-all status:", err);
        stopPolling();
      }
    }, REINDEX_POLL_MS);
  }, [load, stopPolling]);

  useEffect(() => {
    adminService
      .getRagReindexAllStatus()
      .then((status) => {
        setReindexStatus(status);
        if (status.state === "running") {
          pollStatus();
        }
      })
      .catch((err) => console.error("Failed to load reindex-all status:", err));
    return () => stopPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleReindexOne = async (item: { id: string; collection: string }) => {
    setBusyId(item.id);
    setRowResult((prev) => {
      const next = { ...prev };
      delete next[item.id];
      return next;
    });
    try {
      const result = await adminService.reindexDealPinecone(item.collection, item.id);
      setRowResult((prev) => ({ ...prev, [item.id]: result.success ? "ok" : "fail" }));
      if (result.success) {
        await load();
        setMissingItems((prev) => (prev ? prev.filter((m) => m.id !== item.id) : prev));
      }
    } catch (err) {
      console.error("Failed to reindex deal:", err);
      setRowResult((prev) => ({ ...prev, [item.id]: "fail" }));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="p-6">
      <AdminPageHeader
        className="mb-4"
        title="Indexing"
        subtitle={
          <>
            Deals whose last Pinecone index attempt failed, auto-retried in the background every 30 minutes (or
            reindex one now with the per-row button). The full Mongo-vs-Pinecone diff (catching gaps with no failure
            flag at all) runs automatically once a day only.
          </>
        }
        actions={
          <button
            onClick={() => void load()}
            className="px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-800 text-gray-200 hover:bg-gray-700"
          >
            Refresh
          </button>
        }
      />

      {config && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
          <div className="rounded-lg border border-gray-700 bg-gray-900 p-3">
            <p className="text-[11px] text-gray-500">MongoDB database</p>
            <p className="text-sm font-semibold text-white truncate">{config.db_name}</p>
          </div>
          <div className="rounded-lg border border-gray-700 bg-gray-900 p-3">
            <p className="text-[11px] text-gray-500">Pinecone index</p>
            <p className="text-sm font-semibold text-white truncate">{config.pinecone_index}</p>
          </div>
          <div className="rounded-lg border border-gray-700 bg-gray-900 p-3">
            <p className="text-[11px] text-gray-500">Pinecone region</p>
            <p className="text-sm font-semibold text-white truncate">{config.pinecone_environment}</p>
          </div>
          <div className="rounded-lg border border-gray-700 bg-gray-900 p-3">
            <p className="text-[11px] text-gray-500">Embed model</p>
            <p className="text-sm font-semibold text-white truncate">{config.embed_model}</p>
          </div>
          <div className="rounded-lg border border-gray-700 bg-gray-900 p-3">
            <p className="text-[11px] text-gray-500">Embed dimension</p>
            <p className="text-sm font-semibold text-white">{config.embed_dim}</p>
          </div>
        </div>
      )}

      <div className="rounded-lg border border-gray-700 bg-gray-900 p-4 mb-4">
        <p className="text-sm font-semibold text-white">Background reindex status</p>
        <p className="text-xs text-gray-400 mt-0.5">
          {reindexStatus?.state === "running" &&
            (reindexStatus.kind === "scheduled_full_scan"
              ? "Scheduled 24h scan running now…"
              : "Auto-retry (30-min) running now…")}
          {reindexStatus?.state === "success" && reindexStatus.kind === "scheduled_flagged_retry" &&
            `Last auto-retry (30-min): ${reindexStatus.retried ?? 0} flagged deal(s) retried, ${reindexStatus.ok ?? 0} fixed` +
              (reindexStatus.fail ? `, ${reindexStatus.fail} still failing` : "") +
              (reindexStatus.finished_at ? ` · ${formatIST(reindexStatus.finished_at)}` : "")}
          {reindexStatus?.state === "success" && reindexStatus.kind === "scheduled_full_scan" &&
            `Last scheduled scan: ${reindexStatus.gaps_found ?? 0} gap(s) found, ${reindexStatus.ok ?? 0} fixed` +
              (reindexStatus.fail ? `, ${reindexStatus.fail} still failing` : "") +
              (reindexStatus.finished_at ? ` · ${formatIST(reindexStatus.finished_at)}` : "")}
          {reindexStatus?.state === "partial" &&
            `Scheduled scan in progress (checkpointed): ${reindexStatus.remaining ?? 0} gap(s) still queued, ` +
              `${reindexStatus.ok ?? 0} fixed so far this cycle` +
              (reindexStatus.fail ? `, ${reindexStatus.fail} still failing` : "") +
              " — resumes automatically, no data lost between ticks."}
          {reindexStatus?.state === "skipped" && `Last scheduled scan skipped: ${reindexStatus.error || "unknown reason"}`}
          {reindexStatus?.state === "failed" && `Last run failed: ${reindexStatus.error || "unknown error"}`}
          {(!reindexStatus || reindexStatus.state === "idle") && "No background run yet."}
        </p>
      </div>

      <div className="rounded-lg border border-gray-700 bg-gray-900 p-4 mb-6">
        <div className="flex items-center justify-between gap-4 mb-1">
          <div>
            <p className="text-sm font-semibold text-white">Missing from Pinecone</p>
            <p className="text-xs text-gray-400 mt-0.5">
              Catches gaps with no failure flag at all (e.g. a vector removed straight from Pinecone). Read-only —
              from the scheduled once-daily full diff, not a live scan.
              {missingScannedAt && ` Last scanned ${formatIST(missingScannedAt)}.`}
            </p>
          </div>
          <button
            onClick={() => void loadMissingScan()}
            disabled={missingLoading}
            className="px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-800 text-gray-200 hover:bg-gray-700 disabled:opacity-50 shrink-0"
          >
            Refresh
          </button>
        </div>
        {missingError && <p className="text-sm text-red-400 mt-2">{missingError}</p>}
        {missingLoading && missingItems === null ? (
          <p className="text-sm text-gray-400 mt-3">Loading…</p>
        ) : missingScannedAt === null && !missingLoading ? (
          <p className="text-sm text-gray-400 mt-3">Scheduled scan hasn't run yet.</p>
        ) : (
          missingItems !== null && (
            <div className="mt-3 space-y-2">
              {missingSkipped ? (
                <p className="text-sm text-amber-400">
                  Scan skipped — PINECONE_API_KEY not configured on this deployment. This is not "in sync".
                </p>
              ) : missingItems.length === 0 ? (
                <p className="text-sm text-gray-400">Nothing missing — Pinecone matches Mongo.</p>
              ) : (
                missingItems.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between gap-4 rounded-lg border border-gray-700 bg-gray-800 px-3 py-2"
                  >
                    <div className="min-w-0">
                      <p className="text-sm text-white truncate max-w-xl">{item.title}</p>
                      <p className="text-[11px] text-gray-400">
                        {item.collection} → {item.still_failing ? "still missing in" : "was missing in"}:{" "}
                        {item.missing_namespaces.join(", ")}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {item.still_failing ? (
                        <button
                          onClick={() => void handleReindexOne(item)}
                          disabled={busyId === item.id}
                          className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-gray-700 text-white hover:bg-gray-600 disabled:opacity-50"
                        >
                          {busyId === item.id ? "Reindexing…" : "Reindex"}
                        </button>
                      ) : (
                        <span className="text-[11px] text-green-400">Fixed by scheduled run ✓</span>
                      )}
                      {rowResult[item.id] === "ok" && <span className="text-[11px] text-green-400">Indexed ✓</span>}
                      {rowResult[item.id] === "fail" && <span className="text-[11px] text-red-400">Failed again</span>}
                    </div>
                  </div>
                ))
              )}
            </div>
          )
        )}
      </div>

      <h2 className="text-sm font-semibold text-white mb-2">Failed attempts</h2>

      {error && <p className="text-sm text-red-400 mb-3">{error}</p>}

      {loading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-gray-400">No failed indexing deals right now.</p>
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <div key={item.id} className="rounded-lg border border-gray-700 bg-gray-900 p-4">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-white truncate max-w-2xl">{item.title}</p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {item.collection} → ns:{item.namespace || "—"}
                    {item.failed_namespaces.length > 0 && ` (failed in: ${item.failed_namespaces.join(", ")})`}
                  </p>
                  <span
                    className={`inline-block mt-1.5 px-2 py-0.5 rounded text-[11px] font-medium ${
                      CATEGORY_COLOR[item.error_category] || CATEGORY_COLOR.other
                    }`}
                  >
                    {CATEGORY_LABEL[item.error_category] || item.error_category}
                  </span>
                  <p className="text-xs text-gray-500 mt-1.5 truncate max-w-2xl" title={item.error}>
                    {item.error || "—"}
                  </p>
                  {item.failed_at && (
                    <p className="text-[11px] text-gray-600 mt-1">Failed {formatIST(item.failed_at)}</p>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <button
                    onClick={() => void handleReindexOne(item)}
                    disabled={busyId === item.id}
                    className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-gray-700 text-white hover:bg-gray-600 disabled:opacity-50"
                  >
                    {busyId === item.id ? "Reindexing…" : "Reindex"}
                  </button>
                  {rowResult[item.id] === "ok" && <span className="text-[11px] text-green-400">Indexed ✓</span>}
                  {rowResult[item.id] === "fail" && <span className="text-[11px] text-red-400">Failed again</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {total > 0 && (
        <Pagination
          page={page - 1}
          pageSize={pageSize}
          total={total}
          onPageChange={(p) => setPage(p + 1)}
          onPageSizeChange={(s) => {
            setPageSize(s);
            setPage(1);
          }}
        />
      )}
    </div>
  );
}
